import React from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Formik} from "formik";
import {object, string} from "yup";
import Grid from "@mui/material/Grid2";
import {Alert} from "@mui/material";
import {Description} from "@mui/icons-material";
import PageTitle from "./layout/PageTitle.js";
import SubmitButton from "./layout/forms/SubmitButton.js";
import Loader from "./layout/loading/loader.js";
import ModeOperatoireForm from "./referentiel/modesOperatoires/ModeOperatoireForm.js";
import ModeOperatoireFichiersField from "./referentiel/modesOperatoires/ModeOperatoireFichiersField.js";
import Constants from "../../utils/constants.js";
import {toastHelper} from "../../utils/toast-helper.js";
import {
    useGetModeOperatoireQuery,
    useCreateModeOperatoireMutation,
    useUpdateModeOperatoireMutation,
    useGetTypologiesQuery,
} from "../../modules/modesOperatoires/modesOperatoires.api.js";

const schema = object({
    libelle: string().required("Le libellé est requis").max(255, "255 caractères max"),
});

export default function ModeOperatoireNewOrEdit() {
    const {id} = useParams();
    const mode = id ? "edit" : "create";
    const navigate = useNavigate();

    const {data: modeOp, isLoading} = useGetModeOperatoireQuery(id, {skip: !id});
    const {data: typologies = []} = useGetTypologiesQuery();
    const [createModeOperatoire] = useCreateModeOperatoireMutation();
    const [updateModeOperatoire] = useUpdateModeOperatoireMutation();

    const initialValues = {
        libelle: modeOp?.libelle ?? "",
        typologieLavage: modeOp?.typologieLavage?.["@id"] ?? null,
    };

    const handleSubmit = async (values, {setSubmitting}) => {
        // Relation optionnelle : "" (option « Aucune ») -> null pour l'API.
        const payload = {...values, typologieLavage: values.typologieLavage || null};
        try {
            if (mode === "create") {
                const created = await createModeOperatoire(payload).unwrap();
                toastHelper.displaySuccess("Mode opératoire créé. Ajoutez maintenant les fichiers PDF.");
                navigate(Constants.APP_ROUTE_ADMIN_MODES_OPERATOIRES_EDIT.replace(":id", created.id));
            } else {
                await updateModeOperatoire({id, ...payload}).unwrap();
                toastHelper.displaySuccess("Mode opératoire modifié.");
                // Convention de l'app : après un CRUD, retour à la liste (la création reste
                // sur la fiche : les fichiers PDF ne s'ajoutent qu'à un mode op existant)
                navigate(`${Constants.APP_ROUTE_ADMIN_REFERENTIEL}?tab=modes-operatoires`);
            }
        } catch (e) {
            toastHelper.handleError(e, "Oops, une erreur est survenue.");
        } finally {
            setSubmitting(false);
        }
    };

    if (id && isLoading) return <Loader/>;

    return (
        <>
            <PageTitle
                title={mode === "create" ? "Nouveau mode opératoire" : "Modifier le mode opératoire"}
                icon={<Description/>}
            />
            <Formik
                initialValues={initialValues}
                enableReinitialize={true}
                validationSchema={schema}
                onSubmit={handleSubmit}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <ModeOperatoireForm {...formik} typologies={typologies}/>
                        <Grid container justifyContent="center" style={{marginTop: "2rem"}}>
                            <Grid size={4}>
                                <SubmitButton submitting={formik.isSubmitting} label={mode === "create" ? "Créer et ajouter des fichiers" : "Enregistrer"}/>
                            </Grid>
                        </Grid>
                    </form>
                )}
            </Formik>

            {mode === "edit"
                ? <ModeOperatoireFichiersField modeOperatoireId={id} modeOperatoireIri={modeOp?.["@id"]} fichiers={modeOp?.fichiers ?? []}/>
                : <Alert severity="info" sx={{mt: 3}}>Enregistrez le mode opératoire pour pouvoir y ajouter des fichiers PDF.</Alert>}
        </>
    );
}
